// Cancellation copy, mirroring the reply's vocabulary: what will be (or was) cancelled, for
// how much, and when it was due to send — built only from the resolved signal's params, never
// guessed. Plain module (no component imports) so it stays unit-testable.
const CANCEL_KIND_TEXT = {
  PaymentPlan: "payment plan",
  PromiseToPay: "promise to pay",
  // A merchant-scheduled send, not a customer commitment — its own wording and visual identity
  // distinct from a promise to pay.
  ScheduledPaymentRequest: "scheduled payment request",
  PaymentRequest: "payment request",
};

function formatMoney(value) {
  const number = Number(value);
  return Number.isFinite(number)
    ? `£${number.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null;
}

function formatDay(isoDate) {
  const date = new Date(isoDate);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function cancellationTarget(params) {
  const kind = CANCEL_KIND_TEXT[params?.targetKind] ?? "scheduled work";
  const amount = params?.amount != null ? formatMoney(params.amount) : null;
  const day = params?.scheduledFor ? formatDay(params.scheduledFor) : null;
  return {
    kind,
    target: `${kind}${amount ? ` for ${amount}` : ""}${day ? `, scheduled for ${day}` : ""}`,
  };
}

// The heading noun for a cancellation — "payment plan" / "promise to pay" / "payment request",
// or a neutral fallback. Used for the confirmation modal's title and its danger button.
export function cancellationKind(params) {
  return CANCEL_KIND_TEXT[params?.targetKind] ?? "scheduled work";
}

// The red confirmation banner: what is irreversibly torn down once the cancel is confirmed. A
// plan drops its whole schedule; anything else drops just its one calendar event + request.
export function cancellationBanner(params) {
  return params?.targetKind === "PaymentPlan"
    ? "All calendar events & scheduled payment requests associated with this payment plan will be permanently deleted."
    : "The calendar event & scheduled payment request will be permanently deleted.";
}

// The suggestion card's body — what Confirm will do. Mirrors the cancelled item's own
// description; the confirm modal's red banner spells out the consequences.
export function cancellationText(params) {
  const { target } = cancellationTarget(params);
  return params?.scope === "FutureInstallments"
    ? `Cancel the remaining instalments of the ${target}.`
    : `Cancel the ${target}.`;
}

// The optimistic feed row shown while the action→activities pipeline catches up, matching
// the shape the real ActionCancelled activity lands with: "<Kind> cancelled" + a
// "No longer active: …" body built from the same resolved params as the card copy.
export function cancelledActivityCopy(params) {
  const { kind, target } = cancellationTarget(params);
  const headline = kind.charAt(0).toUpperCase() + kind.slice(1);
  return params?.scope === "FutureInstallments"
    ? {
        title: `${headline} instalments cancelled`,
        body: `No longer active: the remaining instalments of the ${target}.`,
      }
    : {
        title: `${headline} cancelled`,
        body: `No longer active: the ${target}.`,
      };
}
